import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getUserManagerAsync } from '../Components/OidcConfig';
import { jwtDecode } from "jwt-decode";
import { useApplication } from './ApplicationContext';

export const ApiContext = createContext();

export const ApiContextProvider = ({ children }) => {
    const { setError } = useApplication();
    
    const [isRefreshingToken, setIsRefreshingToken] = useState(false);
    const apiClient = axios.create({baseURL: process.env.REACT_APP_API_URL});

    useEffect(() => {
      const interval = setInterval(async () => {
          try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/health/service-check`);
            setError([]);
            
            if (response.status !== 200) {
              setError("Service is unavailable or under maintenance");
              return;
            }
          } catch(error) {
            setError("Service is unavailable or under maintenance");
            return;
          }
          try {
            var response = await apiClient.get('/api/health/user-check');
            setError([]);
            if (response.status !== 200) {
              setError("User authentication healthcheck failed returning status code " + response.status);
              return;
            }

          } catch(error) {
            if(error?.response?.status === 401) {
              setError("User authentication failed");
            }

            setError("User authentication healthcheck is unavailable or under maintenance");
          }

      }, 10000); 

      return () => clearInterval(interval); // Cleanup on unmount
  }, []);

    apiClient.interceptors.request.use(
      async (config) => {
        const headers = await getAuthHeadersAsync();
        config.headers = { ...config.headers, ...headers };
        return config;
      },
      (error) => Promise.reject(error)
      );
  
    const getAuthHeadersAsync = async () => 
      {
          const userManager = await getUserManagerAsync();
          let user = await userManager.getUser();
          if (!user || (isTokenCloseToExpire(user.access_token) && !isRefreshingToken) ) {
              setIsRefreshingToken(true);
              console.log("Token close to expire, refreshing");
             
              try{
                user = await userManager.signinSilent();
              }
              catch(error) {                
                console.log("Error refreshing token", error);
                user = await userManager.signinRedirect();
              }  
              setIsRefreshingToken(false);
          }
          return { 'Authorization': `Bearer ${user.access_token}`,'Content-Type': 'application/json'};
      };
      
      const isTokenCloseToExpire = (token) => {
          const refreshTokenWindowMin = 5;
          const {exp, iat} = jwtDecode(token);
          const now = Date.now();
          const expVirtual = exp * 1000 - refreshTokenWindowMin*60*1000;
          const needUpdate = expVirtual < now;
          return needUpdate;
        };

    const testTranscriptionPostProcessingAsync = async (testData, testPrompt = '') => 
        (await apiClient.post('/api/configuration/transcription-processing-test', { testData, testPrompt })).data;

    const testSummaryPromptAsync = async (testData, testPrompt = '') => 
        (await apiClient.post('/api/configuration/summary-test', { testData, testPrompt })).data;

    const testCaseNotesPromptAsync = async (testData, testPrompt = '') => 
        (await apiClient.post('/api/configuration/casenotes-test',{testData, testPrompt})).data;
    
    const upsertPromptCongigAsync = async (summaryPrompt, caseNotesPrompt, transcriptionPrompt) => 
        (await apiClient.put('/api/configuration', { summaryPrompt, caseNotesPrompt, transcriptionPrompt })).data;
 
    const getPromptCongigAsync = async () => 
        (await apiClient.get('/api/configuration')).data;
    
    const getCaseNotesAsync = async (transcriptionId) => 
        (await apiClient.get(`/api/transcriptions/${transcriptionId}/caseNotes`)).data;
    
    const getSummaryAsync = async (transcriptionId) => 
        (await apiClient.get(`/api/transcriptions/${transcriptionId}/summary`)).data;
    
    const postTranscriptionAsync = async (transcription, transcriptionId, reference, speechSessionIds) => {
        const method = transcriptionId ? 'put' : 'post';
        const url = transcriptionId
            ? `/api/transcriptions/${transcriptionId}`
            : `/api/transcriptions`;
        return  (await apiClient[method](url, { transcription, reference, speechSessionIds })).data.id;
    };
    
    const getCognitiveServicesTokenAndRegionAsync = async () => 
        (await apiClient.post('/api/conversationtranscriber/token', {})).data;
    
    const getHistoricalTranscriptionsAsync = async (page, perPage, search) => {
        const params = new URLSearchParams({
            page,
            perPage,
            userName: search || ''
        });
        return (await apiClient.get(`/api/transcriptions?${params}`)).data;
        
    };

  return (
    <ApiContext.Provider value={{ 
        testSummaryPromptAsync,
        testCaseNotesPromptAsync,
        upsertPromptCongigAsync,
        getPromptCongigAsync,
        getCaseNotesAsync,
        getSummaryAsync,
        postTranscriptionAsync,
        getCognitiveServicesTokenAndRegionAsync,
        getHistoricalTranscriptionsAsync,
        testTranscriptionPostProcessingAsync }}>
      {children}
    </ApiContext.Provider>
  );
};

